/* eslint-disable @typescript-eslint/no-unsafe-call */
import { IsString } from "@wog/mol-lib-api-contract/utils";
import { acpV3PropsMaxLengthV2 } from "app/common/constant/acp";
import { plainToInstance, Type } from "class-transformer";
import {
	ArrayMinSize,
	IsBoolean,
	IsEnum,
	IsNumber,
	IsOptional,
	validate,
	ValidateIf,
	ValidateNested,
} from "class-validator";
import { IsEmail } from "@wog/mol-lib-api-contract/utils/validators";
import { IsLocalPhoneNumber, IsNRIC } from "common/validators";
import { nhsRelationship } from "./acpDraft";

class PersonalDetailsRegisteredAddressDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.block })
	block!: string;
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.street })
	street!: string;
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.level })
	floor!: string;
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.unit })
	unit!: string;
	@IsString({
		maxLength: acpV3PropsMaxLengthV2.personalDetails.postalCode,
		minLength: acpV3PropsMaxLengthV2.personalDetails.postalCode,
	})
	postal!: string;
}

export class NHSSpokesPersonDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.name })
	name!: string;

	@IsEmail({ maxLength: acpV3PropsMaxLengthV2.nhs.email })
	email!: string;

	@IsNRIC()
	nric!: string;

	@IsString({ isOptional: false, maxLength: acpV3PropsMaxLengthV2.nhs.dob })
	dob!: string;

	@IsString({ isOptional: false, maxLength: acpV3PropsMaxLengthV2.nhs.mobile })
	mobile!: string;

	@IsEnum(nhsRelationship)
	relationship!: nhsRelationship;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.otherRelationship })
	@ValidateIf((spokesperson) => spokesperson.relationship === "Other")
	otherRelationship!: string;

	@IsOptional()
	createdAt!: string;

	@IsOptional()
	updatedAt!: string;
}

export class NHSSubmitDtoV2 {
	@Type(() => NHSSpokesPersonDtoV2)
	@ValidateNested()
	spokesPersons!: NHSSpokesPersonDtoV2[];
}

export class PersonalDetailsSubmitDtoV2 {
	@IsString({ isOptional: false, maxLength: acpV3PropsMaxLengthV2.personalDetails.name })
	name!: string;

	@IsNRIC()
	uinfin!: string;

	@IsString({ isOptional: false, maxLength: acpV3PropsMaxLengthV2.personalDetails.dob })
	dob!: string;

	@IsString({ isOptional: false, maxLength: acpV3PropsMaxLengthV2.personalDetails.gender })
	sex!: string;

	@IsLocalPhoneNumber(true)
	@IsString({
		isOptional: false,
		maxLength: acpV3PropsMaxLengthV2.personalDetails.phone,
		minLength: acpV3PropsMaxLengthV2.personalDetails.phone,
	})
	mobileNo!: string;

	@IsEmail({ isOptional: false, maxLength: acpV3PropsMaxLengthV2.personalDetails.email })
	email!: string;

	@IsEnum(["ALIEN", "CITIZEN", "PR", "UNKNOWN", "NOT APPLICABLE"])
	residentialStatus!: "ALIEN" | "CITIZEN" | "PR" | "UNKNOWN" | "NOT APPLICABLE";

	@IsString({ isOptional: false })
	race!: string;

	@IsString({ isOptional: false })
	@IsEnum(["MARRIED", "SINGLE", "DIVORCED", "WIDOWED"])
	marital!: "MARRIED" | "SINGLE" | "DIVORCED" | "WIDOWED";

	@ValidateIf((details) => !!details.religion)
	@IsEnum([
		"BUDDHISM",
		"CHRISTIANITY",
		"HINDUISM",
		"ISLAM",
		"ROMANCATHOLIC",
		"TAOISM",
		"SIKHISM",
		"FREETHINKER",
		"OTHERS",
	])
	religion?:
		| "BUDDHISM"
		| "CHRISTIANITY"
		| "HINDUISM"
		| "ISLAM"
		| "ROMANCATHOLIC"
		| "TAOISM"
		| "SIKHISM"
		| "FREETHINKER"
		| "OTHERS";

	@Type(() => PersonalDetailsRegisteredAddressDtoV2)
	@ValidateNested()
	registeredAddress?: PersonalDetailsRegisteredAddressDtoV2;

	@IsString({ isOptional: true })
	passtype?: string;
}

export const ValidateSubmitFormDTOV2 = async (form: any): Promise<boolean> => {
	try {
		const personalDetails = plainToInstance(PersonalDetailsSubmitDtoV2, form);
		const errors = await validate(personalDetails);
		if (errors.length > 0) {
			return false;
		}

		return true;
	} catch (e) {
		return false;
	}
};

class HealthConcernsEnumList {
	@IsEnum(["CONCERNS", "FAMILY_MEDICAL_ISSUE", "MEDICAL_CONDITION", "ALLERGY", "PAST_HEALTH_CONDITION", "OTHERS"])
	concernType!: string;

	@ValidateIf((healthConcern) => !!healthConcern.concernType)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.healthConcerns.description })
	description!: string;

	@IsBoolean()
	isSelected!: boolean;
}

export class HealthConcernSubmitDtoV2 {
	@ValidateIf((healthConcern) => !!healthConcern.isConcerned)
	@IsEnum(["YES", "NO"])
	isConcerned!: string;

	// should be empty if no is selected, but we will just validate the field to be safe
	@ValidateNested({ each: true })
	@Type(() => HealthConcernsEnumList)
	concerns!: HealthConcernsEnumList[];
}

export class PersonalExperienceSubmitDtoV2 {
	@ValidateIf((personalExperiences) => !!personalExperiences.hasExperiences)
	@IsEnum(["YES", "NO"])
	hasExperiences!: string;

	// should be empty if no is selected, but we will just validate the field to be safe
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalExperience.description })
	statement!: string;
}

class OtherThingsYouEnjoyInLife {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.thingsYouEnjoyInLife.otherSelection })
	OTHERS_ACTIVITY!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.thingsYouEnjoyInLife.description })
	OTHERS_DESCRIPTION_IMPORTANCES!: string;
}

export class ThingsYouEnjoyInLifeSubmitDtoV2 {
	@IsEnum(
		[
			"DAILY_TASKS",
			"SPENDING_TIME",
			"CARING",
			"EXCERCISING",
			"TRAVELLING",
			"VOLUNTEERING",
			"HOBBIES",
			"SPIRITUAL",
			"WORKING",
			"OTHERS",
		],
		{ each: true },
	)
	@ArrayMinSize(1)
	selections!: string[];

	@ValidateNested({ each: true })
	@ValidateIf((thingsYouEnjoy) => thingsYouEnjoy.selections.includes("OTHERS"))
	@Type(() => OtherThingsYouEnjoyInLife)
	selectionsDetail!: OtherThingsYouEnjoyInLife;
}

class FearsList {
	@IsEnum(["PHYSICAL_PAIN", "COST", "IMPACT", "LOSS_OF_INDEPENDENCE", "ALONE", "FEAR_OTHERS"])
	fearType!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.fears.description, isOptional: true })
	description!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.fears.otherFear })
	@ValidateIf((fear) => fear.fearType === "FEAR_OTHERS")
	othersType!: string;

	@ValidateIf((fear) => !!fear.isSelectedOther)
	@IsBoolean()
	isSelectedOther!: boolean;
}

export class SourcesOfSupportDtoV2 {
	@IsEnum(["FAMILY", "FRIENDS", "LEISURE", "RELIGION", "OTHERS"], { each: true })
	sources!: string[];

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.sourceOfSupport.description })
	description!: string;

	@ValidateIf((Sos: SourcesOfSupportDtoV2) => Sos.sources.includes("OTHERS"))
	@IsString({ maxLength: acpV3PropsMaxLengthV2.sourceOfSupport.otherSource })
	otherSources!: string;
}

export class GoalsOfMedicalCareDtoV2 {
	@IsEnum(["UNDECIDED", "LIFE_SUSTAINING", "COMFORT_FOCUSED"])
	goalsOfCare!: "UNDECIDED" | "LIFE_SUSTAINING" | "COMFORT_FOCUSED";

	@IsString({ maxLength: acpV3PropsMaxLengthV2.goalsOfCare.description })
	description!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.goalsOfCare.procedureToAvoid, isOptional: true })
	outcomesToAvoid?: string;
}

export class FearsSubmitDtoV2 {
	@ValidateNested({ each: true })
	@Type(() => FearsList)
	@ArrayMinSize(1)
	fears!: FearsList[];
}

export class CarePreferenceSelectionOthersDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.carePreference.otherCarePreference })
	CARE_OTHERS_DEC!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.carePreference.description, isOptional: true })
	CARE_OTHERS_ITEMS!: string;
}

export class CarePreferenceSelectionDetailDtoV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PAIN_MANAGEMENT!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	CULTURAL_AND_SPIRITIAL_NEEDS!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	MOBILITY_AND_EXERCISE!: string;

	@ValidateIf((carePref) => "CARE_OTHERS" in carePref)
	@ValidateNested({ each: true })
	@Type(() => CarePreferenceSelectionOthersDtoV2)
	CARE_OTHERS!: CarePreferenceSelectionOthersDtoV2;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	COMPANIONSHIP_AND_EMOTIONAL_SUPPORT!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PERSONAL_HYGIENE!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PERSONAL_COMFORT_ITEMS!: string;
}

export class CarePreferenceSubmitDtoV2 {
	@ArrayMinSize(1)
	@IsEnum(
		[
			"PAIN_MANAGEMENT",
			"CULTURAL_AND_SPIRITIAL_NEEDS",
			"MOBILITY_AND_EXERCISE",
			"CARE_OTHERS",
			"COMPANIONSHIP_AND_EMOTIONAL_SUPPORT",
			"PERSONAL_HYGIENE",
			"PERSONAL_COMFORT_ITEMS",
		],
		{ each: true },
	)
	selections!: string[];

	@ValidateIf((carePref) => carePref.selections.includes("CARE_OTHERS"))
	@ValidateNested({ each: true })
	@Type(() => CarePreferenceSelectionDetailDtoV2)
	selectionsDetail!: CarePreferenceSelectionDetailDtoV2;
}

export class NhsAdditionalRequestSubmitDTOV2 {
	@IsString({ isOptional: true })
	request?: string;
}

export class SubmitAcpFormDtoV2 {
	@IsString()
	draftId!: string;

	@IsString()
	createdAt!: string;

	@IsString()
	updatedAt!: string;

	@IsNumber()
	currentStep!: number;

	@IsNumber()
	latestStep!: number;

	@IsBoolean()
	suitabilityCheck!: boolean;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsSubmitDtoV2)
	personalDetails!: PersonalDetailsSubmitDtoV2;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernSubmitDtoV2)
	healthConcerns!: HealthConcernSubmitDtoV2;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperienceSubmitDtoV2)
	personalExperiences!: PersonalExperienceSubmitDtoV2;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeSubmitDtoV2)
	thingsYouEnjoyInLife!: ThingsYouEnjoyInLifeSubmitDtoV2;

	@ValidateNested({ each: true })
	@Type(() => FearsSubmitDtoV2)
	fears!: FearsSubmitDtoV2;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDtoV2)
	@IsOptional()
	sourcesOfSupport!: SourcesOfSupportDtoV2;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfMedicalCareDtoV2)
	@IsOptional()
	goalsOfCare!: GoalsOfMedicalCareDtoV2;

	@ValidateNested({ each: true })
	@Type(() => CarePreferenceSubmitDtoV2)
	@IsOptional()
	carePreferences!: CarePreferenceSubmitDtoV2;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestSubmitDTOV2)
	@IsOptional()
	nhsAdditionalRequests!: NhsAdditionalRequestSubmitDTOV2;

	@ValidateNested({ each: true })
	@Type(() => NHSSubmitDtoV2)
	@IsOptional()
	nhs!: NHSSubmitDtoV2;
}
